import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";
import ServerStatusBadge from "@/components/feature/ServerStatusBadge";
import { adminApi } from "@/services/api";

const defaultStats: SystemStats = {
  totalUsers: 0, activeUsers: 0, totalJobs: 0, totalClients: 0,
  totalInvoices: 0, storageUsed: "0 MB", storageTotal: "10 GB",
  storagePercent: 0, dbSize: "0 MB", lastBackup: new Date().toISOString(),
  uptime: "—", apiCalls: 0, logsCount: 0,
};

interface SystemStats {
  totalUsers: number;
  activeUsers: number;
  totalJobs: number;
  totalClients: number;
  totalInvoices: number;
  storageUsed: string;
  storageTotal: string;
  storagePercent: number;
  dbSize: string;
  lastBackup: string;
  uptime: string;
  apiCalls: number;
  logsCount: number;
}

interface AdminUser {
  id: number;
  name: string;
  role: string;
  status: string;
  avatar: string;
}

interface AuditLog {
  id: number;
  action: string;
  module: string;
  details: string;
  severity: "info" | "warning" | "critical";
  status: "success" | "failed";
  timestamp: string;
  userAvatar: string;
}

const severityConfig = {
  info: { color: "#3b82f6", bg: "#eff6ff", label: "Info" },
  warning: { color: "#f59e0b", bg: "#fffbeb", label: "Warning" },
  critical: { color: "#ef4444", bg: "#fef2f2", label: "Critical" },
};

export default function AdminOverviewPage() {
  const navigate = useNavigate();

  const [stats, setStats] = useState<SystemStats>(defaultStats);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersData, logsData] = await Promise.allSettled([
        adminApi.getUsers(),
        adminApi.getAuditLogs(1, 10),
      ]);

      if (usersData.status === "fulfilled") {
        const u = usersData.value?.users || usersData.value?.data || usersData.value || [];
        if (Array.isArray(u)) setUsers(u);
      }
      if (logsData.status === "fulfilled") {
        const l = logsData.value?.logs || logsData.value?.data || logsData.value || [];
        if (Array.isArray(l)) setAuditLogs(l);
      }
    } catch {
      // keep mock data
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const recentLogs = auditLogs.slice(0, 6);
  const activeUsers = users.filter((u) => u.status === "Active");
  const suspendedUsers = users.filter((u) => u.status === "Suspended");
  const criticalLogs = auditLogs.filter((l) => l.severity === "critical");

  const statCards = [
    { label: "Total Users", value: stats.totalUsers, sub: `${stats.activeUsers} active`, icon: "ri-user-line", color: "#7c3aed", bg: "#f5f3ff" },
    { label: "Total Jobs", value: stats.totalJobs, sub: "All time", icon: "ri-file-list-3-line", color: "#10b981", bg: "#ecfdf5" },
    { label: "Total Clients", value: stats.totalClients, sub: "Registered", icon: "ri-contacts-book-2-line", color: "#f59e0b", bg: "#fffbeb" },
    { label: "Invoices", value: stats.totalInvoices, sub: "Generated", icon: "ri-bill-line", color: "#3b82f6", bg: "#eff6ff" },
    { label: "Storage Used", value: stats.storageUsed, sub: `of ${stats.storageTotal}`, icon: "ri-hard-drive-2-line", color: "#ef4444", bg: "#fef2f2" },
    { label: "System Uptime", value: stats.uptime, sub: "Last 30 days", icon: "ri-server-line", color: "#10b981", bg: "#ecfdf5" },
  ];

  return (
    <ERPLayout title="Admin Overview" subtitle="System health, user activity, and quick controls">
      <div className="p-6 space-y-6">

        {/* Refresh bar */}
        <div className="flex items-center justify-between">
          <div />
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-3 h-3 flex items-center justify-center">
              <i className={`ri-refresh-line text-xs ${loading ? "animate-spin" : ""}`} />
            </span>
            Refresh
          </button>
        </div>

        {/* Alert banner for critical events */}
        {criticalLogs.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl border" style={{ background: "#fef2f2", borderColor: "#fecaca" }}>
            <span className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: "#fee2e2" }}>
              <i className="ri-alarm-warning-line text-red-500 text-base" />
            </span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-700">{criticalLogs.length} Critical Security Event(s) Detected</p>
              <p className="text-xs text-red-500 mt-0.5">{criticalLogs[0].details}</p>
            </div>
            <button
              onClick={() => navigate("/admin/audit-logs")}
              className="text-xs font-semibold text-red-600 hover:underline cursor-pointer whitespace-nowrap"
            >
              View Logs →
            </button>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100 hover:border-gray-200 transition-all">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: s.bg }}>
                <i className={`${s.icon} text-lg`} style={{ color: s.color }} />
              </div>
              <p className="text-xl font-bold text-gray-900">{s.value}</p>
              <p className="text-xs font-medium text-gray-700 mt-0.5">{s.label}</p>
              <p className="text-xs text-gray-400">{s.sub}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* System Health + Server Status stacked */}
          <div className="space-y-4">
            {/* Server Connection Status */}
            <ServerStatusBadge variant="card" refreshInterval={30000} />

            {/* System Health */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-4">System Health</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-600 font-medium">Storage</span>
                    <span className="font-semibold text-gray-800">{stats.storagePercent}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="h-2 rounded-full bg-amber-500" style={{ width: `${stats.storagePercent}%` }} />
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{stats.storageUsed} of {stats.storageTotal}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "DB Size", value: stats.dbSize, icon: "ri-database-2-line", color: "#7c3aed" },
                    { label: "API Calls", value: stats.apiCalls.toLocaleString(), icon: "ri-code-s-slash-line", color: "#3b82f6" },
                    { label: "Audit Logs", value: stats.logsCount.toLocaleString(), icon: "ri-file-search-line", color: "#f59e0b" },
                    { label: "Uptime", value: stats.uptime, icon: "ri-server-line", color: "#10b981" },
                  ].map((item) => (
                    <div key={item.label} className="p-3 rounded-xl bg-gray-50">
                      <span className="w-6 h-6 flex items-center justify-center mb-1">
                        <i className={`${item.icon} text-sm`} style={{ color: item.color }} />
                      </span>
                      <p className="text-sm font-bold text-gray-800">{item.value}</p>
                      <p className="text-xs text-gray-500">{item.label}</p>
                    </div>
                  ))}
                </div>
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    <span className="text-xs font-semibold text-amber-700">Last Backup</span>
                  </div>
                  <p className="text-xs text-amber-600 mt-0.5">
                    {new Date(stats.lastBackup).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* User Status */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900 text-sm">User Status</h3>
              <button onClick={() => navigate("/admin/users")} className="text-xs font-medium cursor-pointer hover:underline" style={{ color: "#7c3aed" }}>
                Manage →
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[
                { label: "Active", count: activeUsers.length, color: "#10b981", bg: "#ecfdf5" },
                { label: "Inactive", count: users.filter((u) => u.status === "Inactive").length, color: "#64748b", bg: "#f8fafc" },
                { label: "Suspended", count: suspendedUsers.length, color: "#ef4444", bg: "#fef2f2" },
              ].map((s) => (
                <div key={s.label} className="p-3 rounded-xl text-center" style={{ background: s.bg }}>
                  <p className="text-xl font-bold" style={{ color: s.color }}>{s.count}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              {users.slice(0, 5).map((u) => (
                <div key={u.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 cursor-pointer transition-all" onClick={() => navigate("/admin/users")}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: "#7c3aed" }}>
                    {u.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{u.name}</p>
                    <p className="text-xs text-gray-400">{u.role}</p>
                  </div>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${u.status === "Active" ? "bg-amber-500" : u.status === "Suspended" ? "bg-red-500" : "bg-gray-300"}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="font-bold text-gray-900 text-sm mb-4">Quick Actions</h3>
            <div className="space-y-2">
              {[
                { label: "Add New User", icon: "ri-user-add-line", path: "/admin/users", color: "#7c3aed", bg: "#f5f3ff" },
                { label: "Manage Roles", icon: "ri-shield-keyhole-line", path: "/admin/roles", color: "#f59e0b", bg: "#fffbeb" },
                { label: "View Audit Logs", icon: "ri-file-search-line", path: "/admin/audit-logs", color: "#3b82f6", bg: "#eff6ff" },
                { label: "System Settings", icon: "ri-settings-5-line", path: "/admin/settings", color: "#10b981", bg: "#ecfdf5" },
                { label: "Database Backup", icon: "ri-database-2-line", path: "/admin/database", color: "#ef4444", bg: "#fef2f2" },
                { label: "Notification Config", icon: "ri-notification-badge-line", path: "/admin/notifications-config", color: "#64748b", bg: "#f8fafc" },
                { label: "Site Visit Requests", icon: "ri-calendar-event-line", path: "/admin/site-visits", color: "#10b981", bg: "#ecfdf5" },
                { label: "Services Management", icon: "ri-tools-line", path: "/admin/services", color: "#f59e0b", bg: "#fffbeb" },
              ].map((a) => (
                <button
                  key={a.label}
                  onClick={() => navigate(a.path)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 cursor-pointer transition-all text-left"
                >
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: a.bg }}>
                    <i className={`${a.icon} text-sm`} style={{ color: a.color }} />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{a.label}</span>
                  <span className="ml-auto w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-gray-300 text-base" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Audit Logs */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Recent Audit Activity</h3>
              <p className="text-xs text-gray-500 mt-0.5">Latest system events across all modules</p>
            </div>
            <button onClick={() => navigate("/admin/audit-logs")} className="text-xs font-medium cursor-pointer hover:underline whitespace-nowrap" style={{ color: "#7c3aed" }}>
              View all logs →
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {recentLogs.map((log) => {
              const sev = severityConfig[log.severity] || severityConfig.info;
              return (
                <div key={log.id} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition-all">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: "#7c3aed" }}>
                    {log.userAvatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-gray-800">{log.action}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ color: sev.color, background: sev.bg }}>{sev.label}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{log.details}</p>
                  </div>
                  <div className="text-right flex-shrink-0 hidden sm:block">
                    <p className="text-xs font-medium text-gray-600">{log.module}</p>
                    <p className="text-xs text-gray-400">{new Date(log.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}</p>
                  </div>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${log.status === "success" ? "bg-amber-400" : "bg-red-400"}`} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Module Usage */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-bold text-gray-900 text-sm mb-4">Module Activity (This Month)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {[
              { label: "Job Cards", count: stats.totalJobs > 0 ? Math.min(stats.totalJobs, 34) : 34, icon: "ri-file-list-3-line", color: "#10b981" },
              { label: "Clients", count: stats.totalClients > 0 ? Math.min(stats.totalClients, 8) : 8, icon: "ri-contacts-book-2-line", color: "#f59e0b" },
              { label: "Billing", count: stats.totalInvoices > 0 ? Math.min(stats.totalInvoices, 12) : 12, icon: "ri-bill-line", color: "#3b82f6" },
              { label: "Employees", count: stats.totalUsers > 0 ? stats.totalUsers : 10, icon: "ri-team-line", color: "#7c3aed" },
              { label: "Field Track", count: 28, icon: "ri-map-pin-user-line", color: "#ef4444" },
              { label: "Deliverables", count: 15, icon: "ri-folder-3-line", color: "#f59e0b" },
              { label: "Reports", count: 6, icon: "ri-bar-chart-2-line", color: "#10b981" },
              { label: "Notifications", count: stats.logsCount > 0 ? Math.min(stats.logsCount, 47) : 47, icon: "ri-notification-3-line", color: "#64748b" },
            ].map((m) => (
              <div key={m.label} className="p-3 rounded-xl bg-gray-50 text-center">
                <span className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
                  <i className={`${m.icon} text-lg`} style={{ color: m.color }} />
                </span>
                <p className="text-lg font-bold text-gray-800">{m.count}</p>
                <p className="text-xs text-gray-500">{m.label}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </ERPLayout>
  );
}
